import type { CartLineItem } from 'types/cart/CartLineItem';
import type { CartLineItemWithServings } from 'types/cart/CartLineItemWithServings';
import type { MultiVariantProduct } from 'types/product/MultiVariantProduct';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import type { SingleVariantProduct } from 'types/product/SingleVariantProduct';
import type { Subscription } from 'types/product/Subscription';
import type { SubscriptionProduct } from 'types/product/SubscriptionProduct';
import type { Variant } from 'types/product/Variant';
import type { VariantOrSubscription } from 'types/product/VariantOrSubscription';

export function hasServings<T extends { servings?: number | null }>(
  item: T,
): item is T & { servings: number } {
  return 'servings' in item && typeof item.servings === 'number';
}

export function hasSellingPlan(
  item: Subscription | Variant,
): item is Subscription {
  return 'sellingPlan' in item;
}

export function hasMultipleVariants(
  item: ShopifyProduct | undefined,
): item is MultiVariantProduct {
  if (!item) return false;
  return (
    'variants' in item &&
    Array.isArray(item.variants) &&
    item.variants.length > 1
  );
}

export function hasSingleVariant(
  item: ShopifyProduct | undefined,
): item is SingleVariantProduct {
  if (!item) return false;
  return (
    'variants' in item &&
    Array.isArray(item.variants) &&
    item.variants.length === 1
  );
}

export const isSubscriptionProduct = (
  product: ShopifyProduct,
): product is SubscriptionProduct =>
  'subscriptions' in product && Array.isArray(product.subscriptions);

export const isOtpSingleVariant = (
  shopifyProduct: ShopifyProduct | undefined,
) =>
  shopifyProduct &&
  !hasMultipleVariants(shopifyProduct) &&
  !isSubscriptionProduct(shopifyProduct);

export const isOtpMultiVariant = (shopifyProduct: ShopifyProduct | undefined) =>
  shopifyProduct &&
  hasMultipleVariants(shopifyProduct) &&
  !isSubscriptionProduct(shopifyProduct);

export const isOneTimePurchase = (
  lineItem: CartLineItem | CartLineItemWithServings,
) => !lineItem.sellingPlanAllocation;

export const isSubscription = (
  variantOrSub: VariantOrSubscription,
): variantOrSub is Subscription => 'sellingPlan' in variantOrSub;
